import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs/Subscription';
import { IconColor, IconSize } from '../icon/icon.component';

type InputType = 'text' | 'email' | 'search' | 'number';
type InputSize = 'small' | 'mid' | 'large' | 'xlarge' | 'xxlarge';
type BackgroundColor = 'dark' | 'light';

@Component({
  selector: 'fl-input',
  template: `
    <div
      class="InputContainer"
      [ngClass]="{
        'NoFill': backgroundColor === 'dark',
        'BeforeLabelPresent': beforeLabel.length > 0,
        'AfterLabelPresent': afterLabel.length > 0,
        'is-focused': inputFocused === true,
        'is-expandable': isExpandable === true
      }"
      [attr.data-size]="size"
      [attr.data-expanded]="expanded"
      #inputContainer
    >
      <div class="InputLabel BeforeLabel"
        *ngIf="beforeLabel.length > 0"
        [attr.data-size]="size"
      >
        <span class="LabelText"> {{ beforeLabel }} </span>
      </div>

      <fl-button *ngIf="iconStartClickable"
        (click)="handleIconStartClick($event)"
      >
        <fl-icon *ngIf="iconStart"
          class="IconStart"
          [size]="leftIconSize"
          [color]="leftIconColor"
          [name]="iconStart"
        ></fl-icon>
      </fl-button>
      <fl-icon *ngIf="iconStart && !iconStartClickable"
        class="IconStart"
        [size]="leftIconSize"
        [color]="leftIconColor"
        [name]="iconStart"
      ></fl-icon>
      <input #nativeElement
        class="NativeElement"
        [ngClass]="{
          NoFill: backgroundColor === 'dark'
        }"
        [attr.type]="type"
        [attr.placeholder]="placeholder"
        [attr.readonly]="attrReadonly ? true : null"
        [formControl]="control"
        (focus)="this.inputFocused = !this.inputFocused"
        (blur)="this.inputFocused = false"
      >
      <fl-button *ngIf="iconEndClickable"
        (click)="handleIconEndClick($event)"
      >
        <fl-icon *ngIf="iconEnd"
          class="IconEnd"
          [size]="rightIconSize"
          [color]="rightIconColor"
          [name]="iconEnd"
        ></fl-icon>
      </fl-button>
      <fl-icon *ngIf="iconEnd && !iconEndClickable"
        class="IconEnd"
        [size]="rightIconSize"
        [color]="rightIconColor"
        [name]="iconEnd"
      ></fl-icon>

      <div class="InputLabel AfterLabel"
        *ngIf="afterLabel.length > 0"
        [attr.data-size]="size"
      >
        <span class="LabelText"> {{ afterLabel }} </span>
      </div>
    </div>

    <div *ngIf="control.invalid" class="ErrorText">
        {{ error }}
    </div>
  `,
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements OnInit, OnDestroy {
  error: string;
  errorSubscription: Subscription;
  inputFocused = false;
  iconStartClickable = false;
  iconEndClickable = false;
  @Input() expanded = false;

  @Input() attrReadonly?: true;
  @Input() control: FormControl;
  @Input() placeholder: string;
  @Input() size: InputSize = 'mid';
  // Not for general use. This is only for search.
  // If you'd like to use this input, make sure you discuss with UI Eng
  @Input() isExpandable = false;
  @Input() type: InputType = 'text';
  @Input() backgroundColor: BackgroundColor = 'light';
  @Input() iconStart: string;
  @Input() iconEnd: string;
  @Input() leftIconSize = IconSize.MID;
  @Input() leftIconColor = IconColor.INHERIT;
  @Input() rightIconSize = IconSize.MID;
  @Input() rightIconColor = IconColor.INHERIT;
  @Input() beforeLabel = '';
  @Input() afterLabel = '';

  @Output() iconStartClick = new EventEmitter();
  @Output() iconEndClick = new EventEmitter();

  @ViewChild('nativeElement') nativeElement: ElementRef;
  @ViewChild('inputContainer') inputContainer: ElementRef;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.errorSubscription = this.control.statusChanges.subscribe(() => {
      [this.error] = Object.values(this.control.errors || {});
      this.cd.markForCheck();
    });
    // We are able to check for subscriptions to these inputs in order to infer
    // whether or not the icons should be clickable (displayed as buttons)
    this.iconStartClickable = this.iconStartClick.observers.length > 0;
    this.iconEndClickable = this.iconEndClick.observers.length > 0;
  }

  ngOnDestroy() {
    this.errorSubscription.unsubscribe();
  }

  handleIconEndClick(e: MouseEvent) {
    this.iconEndClick.emit(e);
  }

  handleIconStartClick(e: MouseEvent) {
    this.iconStartClick.emit(e);
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/ui/input/input.component.ts